/**
 * Studio Utils - Utilidades compartidas para el sistema de Studios
 */

/**
 * Helper function para detectar si un objeto tiene estructura estructurada de Studio
 * (titulo, puntos_clave, qa_pairs, events, etc.)
 */
function hasStructuredStudioData(obj: any): boolean {
  if (!obj || typeof obj !== 'object') return false;
  return !!(
    obj.puntos_clave ||
    obj.puntos_claves ||
    obj.key_points ||
    obj.qa_pairs ||
    obj.qaPairs ||
    obj.question_answer_pairs ||
    obj.preguntas_respuestas ||
    obj.events ||
    obj.eventos ||
    obj.timeline_events ||
    obj.insights_principales ||
    obj.main_insights ||
    obj.conclusiones ||
    obj.conclusions ||
    obj.recomendaciones ||
    obj.recommendations ||
    obj.contexto ||
    obj.context ||
    obj.hallazgos ||
    obj.findings ||
    obj.implicaciones ||
    obj.implications ||
    // Si tiene titulo/title Y más propiedades además de citations/originalContent/content, probablemente es estructurado
    ((obj.titulo || obj.title) &&
      Object.keys(obj).filter((k) => !['citations', 'originalContent', 'content'].includes(k))
        .length > 1)
  );
}

// Helpers for normalizeStudioResult
const _detectTopLevelIndicators = (rawResult: any, hasAssistantMessageKey: boolean): number => {
  const hasMetadataTopLevel = rawResult.metadata !== undefined && !hasAssistantMessageKey;
  const hasUsageStatsTopLevel = rawResult.usage_stats !== undefined && !hasAssistantMessageKey;
  const hasRagStatsTopLevel = rawResult.rag_stats !== undefined && !hasAssistantMessageKey;
  const hasCached = rawResult.cached !== undefined && !hasAssistantMessageKey;
  const hasGeneratedAt = rawResult.generated_at !== undefined && !hasAssistantMessageKey;
  return [
    hasMetadataTopLevel,
    hasUsageStatsTopLevel,
    hasRagStatsTopLevel,
    hasCached,
    hasGeneratedAt,
  ].filter(Boolean).length;
};

const _isDirectAssistantMessage = (rawResult: any): boolean => {
  const hasContent = rawResult.content !== undefined;
  const hasAssistantMessageKey = rawResult.assistant_message !== undefined;
  const responseCompleteIndicators = _detectTopLevelIndicators(rawResult, hasAssistantMessageKey);
  return hasContent && !hasAssistantMessageKey && responseCompleteIndicators < 3;
};

const _extractCitations = (assistantMessage: any, rawResult: any): Record<string, any> => {
  if (assistantMessage.citations && typeof assistantMessage.citations === 'object') {
    return assistantMessage.citations;
  }
  if (rawResult.citations && typeof rawResult.citations === 'object') {
    return rawResult.citations;
  }
  if (
    assistantMessage.attachments?.citations &&
    typeof assistantMessage.attachments.citations === 'object'
  ) {
    return assistantMessage.attachments.citations;
  }
  if (rawResult.attachments?.citations && typeof rawResult.attachments.citations === 'object') {
    return rawResult.attachments.citations;
  }
  return {};
};

const _extractOriginalContent = (
  assistantMessage: any,
  rawResult: any,
  isDirectAssistantMessage: boolean
): string => {
  if (
    assistantMessage.content &&
    typeof assistantMessage.content === 'string' &&
    assistantMessage.content.trim().length > 0
  ) {
    return assistantMessage.content;
  }
  if (
    rawResult.content &&
    typeof rawResult.content === 'string' &&
    rawResult.content.trim().length > 0 &&
    !isDirectAssistantMessage
  ) {
    return rawResult.content;
  }
  return '';
};

const _extractResultDataFromAttachments = (
  assistantMessage: any,
  rawResult: any,
  isDirectAssistantMessage: boolean
): any => {
  if (assistantMessage.attachments?.studio_metadata?.result_data) {
    return assistantMessage.attachments.studio_metadata.result_data;
  }
  if (rawResult.attachments?.studio_metadata?.result_data && !isDirectAssistantMessage) {
    return rawResult.attachments.studio_metadata.result_data;
  }
  if (rawResult.metadata?.result_data && !isDirectAssistantMessage) {
    return rawResult.metadata.result_data;
  }
  return null;
};

const _parseJsonFromMarkdownBlock = (trimmed: string): any | null => {
  const jsonBlockMatch = trimmed.match(/```(?:json)?\s*\n([\s\S]*?)\n```/);
  if (jsonBlockMatch) {
    try {
      return JSON.parse(jsonBlockMatch[1].trim());
    } catch {
      return null;
    }
  }
  return null;
};

const _parseJsonFromBracePair = (trimmed: string): any | null => {
  const firstBrace = trimmed.indexOf('{');
  const lastBrace = trimmed.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    try {
      return JSON.parse(trimmed.substring(firstBrace, lastBrace + 1));
    } catch {
      return null;
    }
  }
  return null;
};

const _extractResultDataFromContent = (originalContent: string): any | null => {
  const trimmed = originalContent.trim();
  const jsonBlock = _parseJsonFromMarkdownBlock(trimmed);
  if (jsonBlock) return jsonBlock;

  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  return _parseJsonFromBracePair(trimmed);
};

const _tryParseJsonPermissive = (originalContent: string): any | null => {
  const trimmed = originalContent.trim();
  const jsonPattern = /\{[\s\S]*\}/;
  const jsonMatch = trimmed.match(jsonPattern);
  if (jsonMatch) {
    try {
      return JSON.parse(jsonMatch[0]);
    } catch {
      return null;
    }
  }
  return null;
};

const _extractResultDataFromJsonContent = (originalContent: string): any | null => {
  try {
    const result = _extractResultDataFromContent(originalContent);
    if (result) return result;
  } catch (e) {
    console.warn(
      '⚠️ [normalizeStudioResult] First JSON parse attempt failed, trying alternative methods:',
      e
    );
  }

  return _tryParseJsonPermissive(originalContent);
};

const _findLargestJsonObject = (trimmed: string): any | null => {
  const jsonStart = trimmed.indexOf('{');
  if (jsonStart === -1) return null;

  let braceCount = 0;
  let jsonEnd = -1;

  for (let i = jsonStart; i < trimmed.length; i++) {
    if (trimmed[i] === '{') braceCount++;
    if (trimmed[i] === '}') {
      braceCount--;
      if (braceCount === 0) {
        jsonEnd = i;
        break;
      }
    }
  }

  if (jsonEnd !== -1 && jsonEnd > jsonStart) {
    try {
      return JSON.parse(trimmed.substring(jsonStart, jsonEnd + 1));
    } catch {
      return null;
    }
  }
  return null;
};

const _createSimpleResultData = (originalContent: string): any => {
  return {
    originalContent: originalContent,
    content: originalContent,
  };
};

const _ensureResultDataHasContent = (
  resultData: any,
  originalContent: string,
  hasStructure: boolean
): void => {
  if (!hasStructure) {
    if (!resultData.originalContent && originalContent && originalContent.trim().length > 0) {
      resultData.originalContent = originalContent;
    }
    if (!resultData.content) {
      resultData.content = resultData.originalContent || originalContent || '';
    }
    if (resultData.content && !resultData.originalContent) {
      resultData.originalContent = resultData.content;
    }
  } else {
    if (!resultData.content) {
      resultData.content = '';
    }
  }
};

const _validateAndFixResultData = (resultData: any, originalContent: string): any | null => {
  if (!resultData) return null;

  if (typeof resultData === 'string') {
    const contentStr = resultData.trim();
    return contentStr.length > 0 ? { content: contentStr, originalContent: contentStr } : null;
  }

  if (typeof resultData === 'object') {
    const hasStructure = hasStructuredStudioData(resultData);
    _ensureResultDataHasContent(resultData, originalContent, hasStructure);

    if (!hasStructure && !resultData.originalContent && !resultData.content) {
      const contentValue = resultData.originalContent || resultData.content;
      if (typeof contentValue === 'string') {
        const trimmed = contentValue.trim();
        if (
          (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('[') && trimmed.endsWith(']'))
        ) {
          try {
            const parsed = JSON.parse(trimmed);
            Object.assign(resultData, parsed);
            delete resultData.originalContent;
            delete resultData.content;
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
  }

  return resultData;
};

const _buildFullMetadata = (
  rawResult: any,
  assistantMessage: any,
  isDirectAssistantMessage: boolean
): any => {
  return {
    ...(isDirectAssistantMessage ? {} : rawResult.metadata || {}),
    ...(assistantMessage.metadata || {}),
    usage_stats:
      rawResult.usage_stats ??
      assistantMessage.usage_stats ??
      rawResult.metadata?.usage_stats ??
      assistantMessage.metadata?.usage_stats ??
      (isDirectAssistantMessage ? rawResult.usage_stats : undefined),
    rag_stats:
      rawResult.rag_stats ??
      assistantMessage.rag_stats ??
      rawResult.metadata?.rag_stats ??
      assistantMessage.metadata?.rag_stats ??
      (isDirectAssistantMessage ? rawResult.rag_stats : undefined),
  };
};

const _getCachedValue = (rawResult: any, isDirectAssistantMessage: boolean): boolean => {
  return isDirectAssistantMessage ? false : rawResult.cached ?? false;
};

const _getGeneratedAt = (rawResult: any, assistantMessage: any): string => {
  return (
    rawResult.generated_at ??
    assistantMessage.created_at ??
    assistantMessage.generated_at ??
    rawResult.assistant_message?.created_at ??
    rawResult.assistant_message?.generated_at ??
    new Date().toISOString()
  );
};

/**
 * Normaliza el resultado de un Studio desde diferentes formatos posibles del backend.
 *
 * El backend puede devolver el resultado en diferentes estructuras:
 * 1. attachments.studio_metadata.result_data (formato normalizado)
 * 2. metadata.result_data
 * 3. assistant_message.attachments.studio_metadata.result_data
 * 4. content como string JSON en markdown
 * 5. content como objeto directamente
 *
 * @param rawResult - Resultado crudo del backend
 * @returns Resultado normalizado o null si no se puede normalizar
 */
export function normalizeStudioResult(rawResult: any): {
  result_data: any;
  citations: Record<string, any>;
  originalContent: string;
  metadata: any;
  cached: boolean;
  generated_at: string;
} | null {
  if (!rawResult) return null;

  const isDirectAssistantMessage = _isDirectAssistantMessage(rawResult);
  const assistantMessage = isDirectAssistantMessage
    ? rawResult
    : rawResult.assistant_message || rawResult;
  if (!assistantMessage || typeof assistantMessage !== 'object') return null;

  const citations = _extractCitations(assistantMessage, rawResult);
  const originalContent = _extractOriginalContent(
    assistantMessage,
    rawResult,
    isDirectAssistantMessage
  );

  if (!originalContent || originalContent.trim().length === 0) {
    console.warn('⚠️ [normalizeStudioResult] No originalContent found', {
      assistantMessage_has_content: !!assistantMessage.content,
      assistantMessage_content_type: typeof assistantMessage.content,
      assistantMessage_content_length: assistantMessage.content?.length || 0,
      rawResult_has_content: !!rawResult.content,
      isDirectAssistantMessage,
    });
  }

  let resultData = _extractResultDataFromAttachments(
    assistantMessage,
    rawResult,
    isDirectAssistantMessage
  );

  if (!resultData && originalContent) {
    resultData = _extractResultDataFromJsonContent(originalContent);
  }

  if (
    !resultData &&
    rawResult.content &&
    typeof rawResult.content === 'object' &&
    !isDirectAssistantMessage
  ) {
    resultData = rawResult.content;
  }

  if (!resultData && originalContent && originalContent.trim().length > 0) {
    const trimmed = originalContent.trim();
    if (trimmed.includes('{') && trimmed.includes('}')) {
      resultData = _findLargestJsonObject(trimmed);
    }
    if (!resultData) {
      const trimmed = originalContent.trim();
      if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
        resultData = _createSimpleResultData(originalContent);
      }
    }
  }

  if (!resultData && (!originalContent || originalContent.trim().length === 0)) {
    console.warn('⚠️ [normalizeStudioResult] No content found - returning null');
    return null;
  }

  resultData = _validateAndFixResultData(resultData, originalContent);
  if (!resultData) return null;

  if (
    typeof resultData === 'object' &&
    Object.keys(citations).length > 0 &&
    !resultData.citations
  ) {
    resultData.citations = citations;
  }

  const fullMetadata = _buildFullMetadata(rawResult, assistantMessage, isDirectAssistantMessage);
  const cached = _getCachedValue(rawResult, isDirectAssistantMessage);
  const generated_at = _getGeneratedAt(rawResult, assistantMessage);

  return {
    result_data: resultData,
    citations,
    originalContent,
    metadata: fullMetadata,
    cached,
    generated_at,
  };
}

// Helpers for extractContentText
const _isJsonString = (str: string): boolean => {
  const trimmed = str.trim();
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  );
};

const _tryParseJsonString = (str: string): any | null => {
  try {
    return JSON.parse(str.trim());
  } catch {
    return null;
  }
};

const _convertQAToMarkdown = (result: any): string | null => {
  const qaPairs =
    result.qa_pairs ||
    result.qaPairs ||
    result.question_answer_pairs ||
    result.preguntas_respuestas;
  if (!qaPairs || !Array.isArray(qaPairs) || qaPairs.length === 0) return null;

  const sections: string[] = [];
  sections.push(
    result.titulo ? `# ${result.titulo}` : `# Preguntas y Respuestas Importantes del Análisis`
  );

  if (
    result.resumen_contexto &&
    typeof result.resumen_contexto === 'string' &&
    result.resumen_contexto.trim()
  ) {
    sections.push(`\n${result.resumen_contexto.trim()}`);
  }

  const qaSections = qaPairs
    .map((qa: any, index: number) => {
      const pregunta = qa.pregunta || qa.question || `Pregunta ${index + 1}`;
      const respuesta = qa.respuesta || qa.answer || '';
      return respuesta.trim() ? `## ${pregunta}\n\n${respuesta.trim()}` : null;
    })
    .filter(Boolean);

  if (qaSections.length > 0) {
    sections.push(qaSections.join('\n\n---\n\n'));
  }

  if (
    result.insights_clave &&
    Array.isArray(result.insights_clave) &&
    result.insights_clave.length > 0
  ) {
    sections.push(
      `\n## Insights Clave\n\n` + result.insights_clave.map((i: string) => `- ${i}`).join('\n')
    );
  }

  return sections.join('\n\n');
};

const _hasSummaryStructure = (result: any): boolean => {
  return (
    !!(result.puntos_clave && Array.isArray(result.puntos_clave)) ||
    !!(result.puntos_claves && Array.isArray(result.puntos_claves)) ||
    !!(result.key_points && Array.isArray(result.key_points)) ||
    !!(
      result.titulo &&
      (result.insights_principales ||
        result.conclusiones ||
        result.recomendaciones ||
        result.contexto ||
        result.hallazgos ||
        result.implicaciones ||
        result.title)
    ) ||
    !!(
      result.title &&
      (result.insights_principales ||
        result.conclusiones ||
        result.recomendaciones ||
        result.contexto ||
        result.hallazgos ||
        result.implicaciones)
    ) ||
    !!(result.insights_principales && Array.isArray(result.insights_principales)) ||
    !!(result.main_insights && Array.isArray(result.main_insights)) ||
    !!(result.conclusiones && Array.isArray(result.conclusiones)) ||
    !!(result.conclusions && Array.isArray(result.conclusions)) ||
    !!(result.recomendaciones && Array.isArray(result.recomendaciones)) ||
    !!(result.recommendations && Array.isArray(result.recommendations))
  );
};

const _buildSummarySections = (result: any): string[] => {
  const sections: string[] = [];
  if (result.titulo) sections.push(`# ${result.titulo}`);
  if (result.puntos_clave && Array.isArray(result.puntos_clave) && result.puntos_clave.length > 0) {
    sections.push(
      '## Puntos Clave\n\n' + result.puntos_clave.map((p: string) => `- ${p}`).join('\n')
    );
  }
  if (result.cifras_clave && Array.isArray(result.cifras_clave) && result.cifras_clave.length > 0) {
    sections.push(
      '## Cifras Clave\n\n' + result.cifras_clave.map((c: string) => `- ${c}`).join('\n')
    );
  }
  if (
    result.insights_principales &&
    Array.isArray(result.insights_principales) &&
    result.insights_principales.length > 0
  ) {
    sections.push(
      '## Insights Principales\n\n' +
        result.insights_principales.map((i: string) => `- ${i}`).join('\n')
    );
  }
  if (result.contexto && typeof result.contexto === 'string' && result.contexto.trim()) {
    sections.push('## Contexto\n\n' + result.contexto);
  }
  return sections;
};

const _addHallazgosSection = (sections: string[], result: any): void => {
  if (!result.hallazgos) return;
  if (Array.isArray(result.hallazgos) && result.hallazgos.length > 0) {
    sections.push('## Hallazgos\n\n' + result.hallazgos.map((h: string) => `- ${h}`).join('\n'));
  } else if (typeof result.hallazgos === 'string' && result.hallazgos.trim()) {
    sections.push('## Hallazgos\n\n' + result.hallazgos);
  }
};

const _addImplicacionesSection = (sections: string[], result: any): void => {
  if (!result.implicaciones) return;
  if (Array.isArray(result.implicaciones) && result.implicaciones.length > 0) {
    sections.push(
      '## Implicaciones\n\n' + result.implicaciones.map((i: string) => `- ${i}`).join('\n')
    );
  } else if (typeof result.implicaciones === 'string' && result.implicaciones.trim()) {
    sections.push('## Implicaciones\n\n' + result.implicaciones);
  }
};

const _addConclusionesSection = (sections: string[], result: any): void => {
  if (result.conclusiones && Array.isArray(result.conclusiones) && result.conclusiones.length > 0) {
    sections.push(
      '## Conclusiones\n\n' + result.conclusiones.map((c: string) => `- ${c}`).join('\n')
    );
  }
};

const _addRecomendacionesSection = (sections: string[], result: any): void => {
  if (
    result.recomendaciones &&
    Array.isArray(result.recomendaciones) &&
    result.recomendaciones.length > 0
  ) {
    sections.push(
      '## Recomendaciones\n\n' + result.recomendaciones.map((r: string) => `- ${r}`).join('\n')
    );
  }
};

const _convertSummaryToMarkdown = (result: any): string | null => {
  if (!_hasSummaryStructure(result)) return null;

  const sections = _buildSummarySections(result);
  _addHallazgosSection(sections, result);
  _addImplicacionesSection(sections, result);
  _addConclusionesSection(sections, result);
  _addRecomendacionesSection(sections, result);

  return sections.length > 0 ? sections.join('\n\n') : null;
};

const _extractPeriodoStr = (result: any): string | null => {
  if (typeof result.periodo_analizado === 'string') {
    return result.periodo_analizado;
  }
  if (typeof result.periodo_analizado === 'object' && result.periodo_analizado !== null) {
    const periodoObj = result.periodo_analizado as any;
    if (periodoObj.fecha_inicio && periodoObj.fecha_fin)
      return `${periodoObj.fecha_inicio} - ${periodoObj.fecha_fin}`;
    if (periodoObj.inicio && periodoObj.fin) return `${periodoObj.inicio} - ${periodoObj.fin}`;
    if (periodoObj.start && periodoObj.end) return `${periodoObj.start} - ${periodoObj.end}`;
    if (typeof periodoObj.toString === 'function') return periodoObj.toString();
    try {
      const jsonStr = JSON.stringify(periodoObj);
      return jsonStr.length < 200 ? jsonStr : null;
    } catch {
      return null;
    }
  }
  if (result.fecha_inicio && result.fecha_fin) {
    return `${String(result.fecha_inicio)} - ${String(result.fecha_fin)}`;
  }
  return null;
};

const _formatEventToMarkdown = (event: any): string => {
  const fecha = event.fecha || event.fecha_original || '';
  const titulo = event.titulo || '';
  const descripcion = event.descripcion || '';
  const categoria = event.categoria || '';
  const importancia = event.importancia || '';

  const eventParts: string[] = [];
  if (fecha && titulo) {
    eventParts.push(`### ${fecha} - ${titulo}`);
  } else if (fecha) {
    eventParts.push(`### ${fecha}`);
  } else if (titulo) {
    eventParts.push(`### ${titulo}`);
  }

  if (descripcion) eventParts.push(`\n${descripcion.trim()}`);

  if (categoria || importancia) {
    const metaParts: string[] = [];
    if (categoria) metaParts.push(`**Categoría:** ${categoria}`);
    if (importancia) metaParts.push(`**Importancia:** ${importancia}`);
    if (metaParts.length > 0) eventParts.push(`\n${metaParts.join('  ')}`);
  }

  return eventParts.join('\n');
};

const _convertTimelineToMarkdown = (result: any): string | null => {
  const events = result.events || result.eventos || result.timeline_events;
  if (!events || !Array.isArray(events) || events.length === 0) return null;

  const sections: string[] = [];
  const titulo = result.titulo || result.title || result.timeline_title;
  sections.push(titulo ? `# ${titulo}` : `# Línea de Tiempo de Eventos Clave`);

  const periodoStr = _extractPeriodoStr(result);
  if (periodoStr && periodoStr.trim().length > 0) {
    sections.push(`\n**Período analizado:** ${periodoStr.trim()}`);
    if (result.descripcion_periodo) {
      const descStr =
        typeof result.descripcion_periodo === 'string'
          ? result.descripcion_periodo
          : String(result.descripcion_periodo);
      if (descStr.trim().length > 0) sections.push(`${descStr.trim()}`);
    }
  }

  sections.push(`\n## Eventos Cronológicos\n`);
  const eventSections = events.map(_formatEventToMarkdown).filter(Boolean);
  if (eventSections.length > 0) {
    sections.push(eventSections.join('\n\n---\n\n'));
  }

  if (
    result.patrones_identificados &&
    Array.isArray(result.patrones_identificados) &&
    result.patrones_identificados.length > 0
  ) {
    sections.push(
      `\n## Patrones Identificados\n\n` +
        result.patrones_identificados.map((p: string) => `- ${p}`).join('\n')
    );
  }

  if (
    result.resumen_cronologico &&
    typeof result.resumen_cronologico === 'string' &&
    result.resumen_cronologico.trim()
  ) {
    sections.push(`\n## Resumen Cronológico\n\n${result.resumen_cronologico.trim()}`);
  }

  return sections.join('\n\n');
};

const _tryExtractFromContentFields = (result: any): string | null => {
  const contentFields = ['contenido', 'descripcion', 'summary', 'text', 'value', 'response'];
  for (const field of contentFields) {
    if (result[field] && typeof result[field] === 'string' && result[field].trim()) {
      return result[field];
    }
  }
  return null;
};

const _convertGenericObjectToMarkdown = (result: any): string => {
  const keys = Object.keys(result).filter(
    (k) => !['citations', 'originalContent', 'content'].includes(k)
  );
  if (keys.length === 0 || keys.length >= 15) return '';

  if (result.titulo) {
    const parts: string[] = [`# ${result.titulo}`];
    for (const key of keys) {
      if (key !== 'titulo' && result[key] !== undefined && result[key] !== null) {
        const value = result[key];
        if (Array.isArray(value)) {
          parts.push(
            `\n## ${key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}\n\n` +
              value
                .map((item: any) => `- ${typeof item === 'string' ? item : JSON.stringify(item)}`)
                .join('\n')
          );
        } else if (typeof value === 'string' && value.trim()) {
          parts.push(
            `\n## ${key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}\n\n${value}`
          );
        }
      }
    }
    return parts.join('\n\n');
  }

  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return '';
  }
};

/**
 * Extrae el texto de contenido desde un resultado de Studio.
 * Prioriza estructura parseada sobre JSON crudo.
 *
 * El resultado puede venir en diferentes formatos:
 * - Objeto con originalContent/content
 * - String directamente
 * - Objeto estructurado (Q&A, Summary, Timeline, etc.)
 */
export function extractContentText(result: any): string {
  if (!result) return '';

  if (typeof result === 'string') {
    if (_isJsonString(result)) {
      const parsed = _tryParseJsonString(result);
      if (parsed) return extractContentText(parsed);
    }
    return result;
  }

  if (typeof result !== 'object') return '';

  const qaMarkdown = _convertQAToMarkdown(result);
  if (qaMarkdown) return qaMarkdown;

  const summaryMarkdown = _convertSummaryToMarkdown(result);
  if (summaryMarkdown) return summaryMarkdown;

  const timelineMarkdown = _convertTimelineToMarkdown(result);
  if (timelineMarkdown) return timelineMarkdown;

  if (result.originalContent && typeof result.originalContent === 'string') {
    const trimmed = result.originalContent.trim();
    if (!_isJsonString(trimmed)) return result.originalContent;
    const parsed = _tryParseJsonString(trimmed);
    if (parsed) {
      const converted = extractContentText(parsed);
      if (converted && converted.trim().length > 0) return converted;
    }
    return result.originalContent;
  }

  if (result.content && typeof result.content === 'string') {
    const trimmed = result.content.trim();
    if (!_isJsonString(trimmed)) return result.content;
    const parsed = _tryParseJsonString(trimmed);
    if (parsed) {
      const converted = extractContentText(parsed);
      if (converted && converted.trim().length > 0) return converted;
    }
    return result.content;
  }

  const extracted = _tryExtractFromContentFields(result);
  if (extracted) return extracted;

  return _convertGenericObjectToMarkdown(result);
}
